import { message } from 'antd';
import { useCallback } from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import chatttSDK, { syncSysSettings } from 'services/chattt';
import { isUndefined } from 'lodash-es';
import { useTranslate } from '@tolgee/react';
import { tolgee } from 'services/i18n/tolgee';
import { showRequestNotificationDialog } from 'components/NiceMessageDialog/show';
import { isInBrowser } from 'utils/is';
import { useAtomCallback } from 'jotai/utils';
import { setNumberFormatLocale } from 'utils/numberFormat';

type MessageRemind = {
  soundRemind: boolean;
  popupRemind: boolean;
  flashRemind?: boolean;
  reviewRemind?: boolean;
  refundRemind?: boolean;
};

export type SystemLanguage = General.SystemLanguage;

export interface SystemSetting {
  messageRemind: MessageRemind;
  language: SystemLanguage;
  closeType: Chattt.SystemSettings['quitMode'];
  theme: 'Orange' | 'Blue' | 'Purple';
}
const SYSTEM_SETTING_STORAGE_KEY = 'fett/systemSetting';

const effects = (setting: SystemSetting) => {
  localStorage.setItem(SYSTEM_SETTING_STORAGE_KEY, JSON.stringify(setting));

  tolgee.changeLanguage(setting.language);

  syncSysSettings({
    language: setting.language,
    quitMode: setting.closeType,
  });
};

export const defaultRemindSetting: MessageRemind = {
  soundRemind: true,
  popupRemind: true,
  flashRemind: true,
  reviewRemind: true,
  refundRemind: true,
};

function detectLanguage(): General.Language {
  if (navigator.language.includes('zh')) {
    return 'zh-CN';
  }
  if (navigator.language.includes('id')) {
    return 'id';
  }
  if (navigator.language.includes('th')) {
    return 'th';
  }
  if (navigator.language.includes('vi')) {
    return 'vi';
  }
  if (navigator.language.includes('fil')) {
    return 'tl';
  }
  // if (navigator.language.includes('es')) {
  //   return 'es';
  // }
  if (navigator.language.includes('pt')) {
    return 'pt';
  }

  return 'en-US';
}

const getDefaultSystemSetting = (): SystemSetting => {
  const storage = localStorage.getItem(SYSTEM_SETTING_STORAGE_KEY);
  let language: General.Language = detectLanguage();
  let cached: SystemSetting | undefined;
  if (storage) {
    cached = JSON.parse(storage) as SystemSetting;
    /**
     * TODO:
     * 西语不完整，切换为英语
     */
    if (cached.language === 'es') {
      cached.language = 'en-US';
    }

    language = cached.language ?? language;
  }

  tolgee.changeLanguage(language);

  if (cached) {
    effects(cached);
    if (cached.theme !== 'Orange' && cached.theme !== 'Blue') {
      cached.theme = 'Blue';
    }
    return cached;
  }

  const setting: SystemSetting = {
    messageRemind: {
      ...defaultRemindSetting,
      flashRemind: chatttSDK?.app?.getFlashRemind?.() || true,
    },
    language: language as SystemLanguage,
    closeType: 'default',
    theme: 'Blue',
  };

  effects(setting);

  return setting;
};

const systemSettingAtom = atom(getDefaultSystemSetting());
const systemSettingState = atom(
  (get) => get(systemSettingAtom),
  (_, set, next: SystemSetting) => {
    set(systemSettingAtom, next);
    effects(next);
  },
);

export const useGetNotificationPermission = () => {
  return useAtomCallback(
    useCallback((get, set) => {
      if (window.Notification && window.Notification.permission === 'default') {
        if (isInBrowser) {
          showRequestNotificationDialog();
        }
        const systemSetting = get(systemSettingState);
        window.Notification.requestPermission().then((permission) => {
          set(systemSettingState, {
            ...systemSetting,
            messageRemind: {
              ...systemSetting.messageRemind,
              popupRemind: permission === 'granted',
            },
          });
        });
      }
    }, []),
  );
};

const systemLanguageState = atom((get) => get(systemSettingState).language);

function useSystem() {
  const { t } = useTranslate();

  const [systemSetting, setSystemSetting] = useAtom(systemSettingState);

  const handleMessageRemindSettingChange = useCallback(
    async (messageRemind: SystemSetting['messageRemind']) => {
      let result = Notification.permission;
      if (messageRemind.popupRemind && result !== 'granted') {
        result = await window.Notification.requestPermission();
        result === 'denied' &&
          message.warn(t('errorMsg.您已禁用系统通知，请在电脑系统设置中手动开启'));
      }

      // Fixed: 首次渲染 undefined 值导致其他 switch 项无法更新
      if (!isUndefined(messageRemind.flashRemind)) {
        chatttSDK?.app?.setFlashRemind?.(messageRemind.flashRemind);
      }

      setSystemSetting({
        ...systemSetting,
        messageRemind: {
          ...messageRemind,
          popupRemind: messageRemind.popupRemind ? result === 'granted' : false,
        },
      });
    },
    [setSystemSetting, systemSetting, t],
  );
  const handleLanguageSettingChange = useCallback(
    (language: SystemLanguage) => {
      setNumberFormatLocale(language);
      setSystemSetting({
        ...systemSetting,
        language,
      });
    },
    [setSystemSetting, systemSetting],
  );
  const handleCloseSettingChange = useCallback(
    (closeType: SystemSetting['closeType']) => {
      setSystemSetting({
        ...systemSetting,
        closeType,
      });
    },
    [setSystemSetting, systemSetting],
  );
  const handleThemeSettingChange = useCallback(
    (theme: SystemSetting['theme']) => {
      setSystemSetting({
        ...systemSetting,
        theme,
      });
    },
    [setSystemSetting, systemSetting],
  );

  return {
    systemSetting,
    handleMessageRemindSettingChange,
    handleLanguageSettingChange,
    handleCloseSettingChange,
    handleThemeSettingChange,
  };
}

export const useSystemLanguageValue = () => useAtomValue(systemLanguageState);
export const useSystemSettingsValue = () => useAtomValue(systemSettingState);

export default useSystem;
